/**
 * 375 (#1239): the promo dashboard's payload (`GET /promotions/dashboard`, `backend/src/modules/promotions/dashboard.ts`)
 * and the page's pure helpers: the period presets in Malaysia days, the weekly roll-up and the CSV.
 */
export interface Amount {
  count: number;
  value_minor: number;
}

export interface PromoDashboard {
  period: { from: string; to: string };
  previous: { from: string; to: string };
  given: {
    total_minor: number;
    previous_total_minor: number;
    credit_minor: number;
    credit_bookings: number;
    voucher_minor: number;
    voucher_bookings: number;
    voucher_campaigns: number;
    personal_minor: number;
    personal_bookings: number;
    personal_customers: number;
    revenue_minor: number;
  };
  days: Array<{ day: string; credit_minor: number; voucher_minor: number; personal_minor: number }>;
  campaigns: Array<{
    id: string;
    name: string | null;
    discount_type: string | null;
    discount_value: number | null;
    valid_until: string | null;
    deleted: boolean;
    codes_used: number;
    given_minor: number;
    limit: number | null;
    redemptions_count: number;
    at_limit: boolean;
  }>;
  certificates: {
    issued: Amount;
    used: Amount;
    returned: Amount;
    outstanding: Amount;
    expiring_within_30_days: Amount;
    expired_unused: Amount;
  };
  receivers: Array<{
    user_id: string;
    name: string | null;
    bookings: number;
    credit_minor: number;
    voucher_minor: number;
    personal_minor: number;
    total_minor: number;
  }>;
  pending: { certificates: number; certificates_value_minor: number; discounts: number };
}

export type Preset = 'week' | 'month' | 'last-month' | 'custom';

const MYT_OFFSET_MS = 8 * 3_600_000;
const DAY_MS = 86_400_000;
const iso = (t: number) => new Date(t).toISOString().slice(0, 10);

/** Today in Malaysia, `YYYY-MM-DD`. */
export function mytToday(now: Date = new Date()): string {
  return iso(now.getTime() + MYT_OFFSET_MS);
}

/** A preset's From and To (inclusive Malaysia days). The week starts on Monday. */
export function presetRange(
  preset: Exclude<Preset, 'custom'>,
  now: Date = new Date(),
): { from: string; to: string } {
  const today = mytToday(now);
  const t = Date.parse(`${today}T00:00:00Z`);
  const d = new Date(t);
  if (preset === 'week') {
    const back = (d.getUTCDay() + 6) % 7;
    return { from: iso(t - back * DAY_MS), to: today };
  }
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  if (preset === 'month') {
    return { from: iso(Date.UTC(y, m, 1)), to: iso(Date.UTC(y, m + 1, 0)) };
  }
  return { from: iso(Date.UTC(y, m - 1, 1)), to: iso(Date.UTC(y, m, 0)) };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "1 Oct – 31 Oct 2026" / "29 Dec 2025 – 4 Jan 2026". Fixed month names: the browser's own vary ("Sept"). */
export function rangeLabel(from: string, to: string): string {
  const part = (day: string, year: boolean) => {
    const [y, m, d] = day.split('-').map(Number) as [number, number, number];
    return `${d} ${MONTHS[m - 1]}${year ? ` ${y}` : ''}`;
  };
  return `${part(from, from.slice(0, 4) !== to.slice(0, 4))} – ${part(to, true)}`;
}

export const rm = (minor: number) =>
  `RM ${(minor / 100).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/** The change against the previous period, as a whole percentage; null when there is nothing to compare with. */
export function changePercent(now: number, before: number): number | null {
  if (before <= 0) return null;
  return Math.round(((now - before) / before) * 100);
}

/** The share of revenue given away, one decimal; null with no revenue. */
export function sharePercent(given: number, revenue: number): number | null {
  if (revenue <= 0) return null;
  return Math.round((given / revenue) * 1000) / 10;
}

export interface ChartBar {
  label: string;
  from: string;
  to: string;
  credit: number;
  voucher: number;
  personal: number;
}

/** The chart's bars in ringgit: one per day, or one per Monday-start week (a partial first or last week included). */
export function chartBars(days: PromoDashboard['days'], by: 'day' | 'week'): ChartBar[] {
  const bars: ChartBar[] = [];
  for (const d of days) {
    const date = new Date(`${d.day}T00:00:00Z`);
    const startsWeek = by === 'week' && bars.length > 0 && date.getUTCDay() === 1;
    const last = bars[bars.length - 1];
    if (by === 'day' || !last || startsWeek) {
      bars.push({
        label:
          by === 'day'
            ? String(date.getUTCDate())
            : `${date.getUTCDate()}/${date.getUTCMonth() + 1}`,
        from: d.day,
        to: d.day,
        credit: 0,
        voucher: 0,
        personal: 0,
      });
    }
    const bar = bars[bars.length - 1] as ChartBar;
    bar.to = d.day;
    bar.credit += d.credit_minor / 100;
    bar.voucher += d.voucher_minor / 100;
    bar.personal += d.personal_minor / 100;
  }
  return bars;
}

const cell = (v: string | number) => {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const ringgit = (minor: number) => (minor / 100).toFixed(2);

/** The CSV the Export button saves: what was given per day, then the period's totals. */
export function promoCsv(data: PromoDashboard): string {
  const rows: Array<Array<string | number>> = [
    [
      'Day',
      'Credit used (RM)',
      'Voucher discounts (RM)',
      'Personal discounts (RM)',
      'Total given (RM)',
    ],
  ];
  for (const d of data.days) {
    rows.push([
      d.day,
      ringgit(d.credit_minor),
      ringgit(d.voucher_minor),
      ringgit(d.personal_minor),
      ringgit(d.credit_minor + d.voucher_minor + d.personal_minor),
    ]);
  }
  const g = data.given;
  rows.push([
    `Total ${data.period.from} to ${data.period.to}`,
    ringgit(g.credit_minor),
    ringgit(g.voucher_minor),
    ringgit(g.personal_minor),
    ringgit(g.total_minor),
  ]);
  return `${rows.map((r) => r.map(cell).join(',')).join('\n')}\n`;
}
